import React, { useState, useEffect, useRef, useCallback } from 'react';
import { IconMessageChatbot, IconX, IconSend, IconTrash, IconLoader2 } from '@tabler/icons-react';
import api from '../api/api';

/**
 * 홈 화면 전용 AI 골프 챗봇 플로팅 버튼 + 채팅창
 */
function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // [{ role, content, createdAt }]
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const listRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get('/ai-chat/history');
      setMessages(res.data || []);
    } catch {
      // 무시 (빈 대화로 시작)
    } finally {
      setHistoryLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (open && !historyLoaded) loadHistory();
  }, [open, historyLoaded, loadHistory]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open, loading]);

  const send = async () => {
    const question = input.trim();
    if (!question || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'USER', content: question, createdAt: new Date().toISOString() }]);
    setLoading(true);
    try {
      const res = await api.post('/ai-chat/ask', { question });
      setMessages(prev => [...prev, { role: 'ASSISTANT', content: res.data.answer, createdAt: new Date().toISOString() }]);
    } catch {
      setMessages(prev => [...prev, { role: 'ASSISTANT', content: '죄송해요, 답변을 가져오지 못했어요. 잠시 후 다시 시도해주세요.', createdAt: new Date().toISOString() }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const clearHistory = () => {
    if (!window.confirm('화면의 대화 내용을 지울까요? (저장된 대화 기록은 삭제되지 않습니다)')) return;
    setMessages([]);
  };

  return (
    <>
      <style>{`
        @keyframes chatbot-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      {open && (
        <div
          className="card"
          style={{
            position: 'fixed', bottom: 88, right: 20, width: 320, maxWidth: 'calc(100vw - 32px)',
            height: 440, maxHeight: 'calc(100vh - 140px)', zIndex: 1060,
            borderRadius: 14, overflow: 'hidden', boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
            display: 'flex', flexDirection: 'column', border: 'none',
          }}
        >
          <div style={{
            padding: '10px 12px', background: 'linear-gradient(135deg, #206bc4 0%, #206bc4cc 100%)',
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
              <IconMessageChatbot size={18} /> AI 골프 어시스턴트
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button className="btn btn-icon btn-sm" onClick={clearHistory} title="화면 지우기"
                style={{ background: 'rgba(255,255,255,0.25)', border: 'none', color: '#fff', width: 24, height: 24, borderRadius: '50%' }}>
                <IconTrash size={13} />
              </button>
              <button className="btn btn-icon btn-sm" onClick={() => setOpen(false)} title="닫기"
                style={{ background: 'rgba(255,255,255,0.25)', border: 'none', color: '#fff', width: 24, height: 24, borderRadius: '50%' }}>
                <IconX size={14} />
              </button>
            </div>
          </div>

          <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: '10px 10px', background: '#f6f7f9' }}>
            {messages.length === 0 && !loading && (
              <div className="text-muted text-center" style={{ fontSize: 12, marginTop: 20 }}>
                골프 스코어에 대해 궁금한 걸 물어보세요.<br />예) "내 최근 5경기 평균 스코어 알려줘"
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: m.role === 'USER' ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                <div style={{
                  maxWidth: '80%', padding: '7px 10px', borderRadius: 12, fontSize: 12.5, lineHeight: 1.4,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  background: m.role === 'USER' ? '#206bc4' : '#fff',
                  color: m.role === 'USER' ? '#fff' : '#1d273b',
                  border: m.role === 'USER' ? 'none' : '1px solid #e6e7e9',
                }}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
                <div style={{ padding: '7px 10px', borderRadius: 12, background: '#fff', border: '1px solid #e6e7e9' }}>
                  <IconLoader2 size={14} className="spin" style={{ animation: 'spin 1s linear infinite' }} />
                </div>
              </div>
            )}
          </div>

          <div style={{ padding: 8, borderTop: '1px solid #e6e7e9', display: 'flex', gap: 6, background: '#fff' }}>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="질문을 입력하세요..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />
            <button className="btn btn-primary btn-sm d-flex align-items-center" onClick={send} disabled={loading || !input.trim()}>
              <IconSend size={15} />
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        title="AI 골프 어시스턴트"
        style={{
          position: 'fixed', bottom: 20, right: 20, width: 56, height: 56, borderRadius: '50%',
          background: '#206bc4', color: '#fff', border: 'none', boxShadow: '0 6px 16px rgba(0,0,0,0.3)',
          zIndex: 1055, display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: open ? 'none' : 'chatbot-float 2.4s ease-in-out infinite',
        }}
      >
        {open ? <IconX size={26} /> : <IconMessageChatbot size={28} />}
      </button>
    </>
  );
}

export default ChatWidget;
